import * as THREE from 'three';
import { CONFIG } from '../config.js';
import { yawOf } from '../core/directions.js';
import { findHit, pitUnder } from '../systems/Collision.js';
import { Adventurer } from '../render/Adventurer.js';

const P = CONFIG.player;
const LW = CONFIG.path.laneWidth;
const HW = CONFIG.path.width / 2;
const SPRINT_GLOW = new THREE.Color(0xff6a00);

/**
 * Corredor. La lógica vive en coordenadas del camino:
 *   segment (tramo actual), s (metros a lo largo), x (metros a la derecha del eje), y (altura).
 * La malla se sincroniza a partir de ellas en syncMesh().
 *
 * Estados: 'idle' | 'running' | 'falling' | 'crashed' | 'caught'
 * update() devuelve un evento puntual:
 *   'turn' | 'stumble' | 'smash' | 'fell' | 'fellSide' | 'pit' | 'crash' | null
 * Tras 'stumble' o 'smash', lastHit indica el obstáculo implicado.
 */
export class Player {
  constructor(scene) {
    this.turnAssist = 1; // multiplicador de la ventana de giro (lo fija el juego según el control)
    this.position = new THREE.Vector3();
    this.cameraAnchor = new THREE.Vector3();

    this.mesh = new THREE.Group();
    this.mesh.name = 'Player';
    // Modelo low-poly animado. "body" pivota en los pies: se inclina al caer.
    this.model = new Adventurer();
    this.body = this.model.root;
    this.torsoMaterial = this.model.jacket;

    // Indicadores de potenciadores activos.
    this.shieldBubble = new THREE.Mesh(
      new THREE.SphereGeometry(1.25, 14, 10),
      new THREE.MeshLambertMaterial({
        color: 0x4cd8ff,
        emissive: 0x0d4a5a,
        transparent: true,
        opacity: 0.28,
        depthWrite: false,
      }),
    );
    this.shieldBubble.position.y = 0.95;
    this.magnetRing = new THREE.Mesh(
      new THREE.TorusGeometry(0.42, 0.07, 6, 14).rotateX(Math.PI / 2),
      new THREE.MeshLambertMaterial({ color: 0xb04cff, emissive: 0x4a1070 }),
    );
    this.magnetRing.position.y = P.height + 0.45;
    this.mesh.add(this.body, this.shieldBubble, this.magnetRing);
    scene.add(this.mesh);
  }

  reset(path) {
    this.path = path;
    this.distance = 0;
    this.#placeAt(path.first, 0);
    this.state = 'idle';
    this.setEffects({ magnet: 0, shield: 0, sprint: 0 }, 0);
    this.syncMesh(0);
  }

  /** Segunda oportunidad: reaparece corriendo al principio del tramo indicado (conserva la distancia). */
  respawn(segment) {
    this.#placeAt(segment, 1);
    this.state = 'running';
    this.syncMesh(0);
  }

  #placeAt(segment, s) {
    this.segment = segment;
    this.s = s;
    this.x = 0;
    this.lane = 0; // -1 izquierda, 0 centro, 1 derecha
    this.prevLane = 0; // carril de origen del último cambio (para rebotar)
    this.y = 0;
    this.vy = 0;
    this.grounded = true;
    this.slideTimer = 0;
    this.jumpBuffer = 0;
    this.hurtTimer = 0;
    this.queuedTurn = 0;
    this.turnRequest = 0; // giro pedido con el brazo antes de llegar a la ventana de giro
    this.turnRequestTime = 0;
    this.fallSide = 0;
    this.fallPit = null; // foso en el que ha caído
    this.pendingEvent = null;
    this.lastHit = null;
    this.invulnerable = false; // lo fija World según escudo / sprint
    this.pitProof = false; // durante el sprint los fosos no hacen caer (lo fija World)
    this.speed = CONFIG.speed.base;
    this.yaw = yawOf(this.segment.dirIndex);
    this.visualYaw = this.yaw;
    this.body.rotation.set(0, 0, 0);
    this.body.scale.set(1, 1, 1);
  }

  get sliding() {
    return this.slideTimer > 0;
  }

  get height() {
    return this.sliding ? P.slideHeight : P.height;
  }

  /**
   * Ventana de giro en metros: crece con la velocidad para mantener el mismo tiempo de reacción.
   * Con la cámara (turnAssist > 1) es más amplia: el cuerpo reacciona más despacio que un dedo.
   */
  get turnWindow() {
    return (CONFIG.turn.window * this.turnAssist * this.speed) / CONFIG.speed.base;
  }

  /** Metros que faltan hasta el centro de la esquina (negativo si ya se ha pasado). */
  get toCorner() {
    return this.segment.length - this.s;
  }

  start() {
    this.state = 'running';
  }

  /** El perseguidor le ha alcanzado. */
  catchMe() {
    this.state = 'caught';
    this.speed = 0;
  }

  // ---------- Órdenes del jugador ----------

  requestJump() {
    if (this.state !== 'running') return;
    if (this.grounded) this.#jump();
    else this.jumpBuffer = P.jumpBuffer;
  }

  requestSlide() {
    if (this.state !== 'running') return;
    this.slideTimer = P.slideDuration;
    this.jumpBuffer = 0;
    if (!this.grounded) this.vy = Math.min(this.vy, P.fastFallVelocity);
  }

  /**
   * Deslizamiento lateral (dir: -1 izquierda, +1 derecha).
   * Cerca de una esquina y hacia el lado de la curva → gira.
   * En otro caso → cambia de carril; desde un carril exterior hacia fuera → se cae del camino.
   */
  requestLateral(dir) {
    if (this.state !== 'running') return;
    const seg = this.segment;
    const turnable = seg.tee || seg.turn === dir;
    const toCorner = this.toCorner;
    if (turnable && toCorner <= this.turnWindow && toCorner >= -CONFIG.turn.lateGrace) {
      this.queuedTurn = dir;
      return;
    }
    const lane = this.lane + dir;
    if (Math.abs(lane) > 1) {
      this.state = 'falling';
      this.fallSide = dir;
      this.pendingEvent = 'fellSide';
      return;
    }
    this.prevLane = this.lane;
    this.lane = lane;
  }

  /**
   * Carril elegido con la posición del cuerpo (cámara): nunca gira ni hace caer por el lateral,
   * solo cambia de carril (-1, 0, 1).
   */
  setLane(lane) {
    if (this.state !== 'running') return;
    lane = Math.max(-1, Math.min(1, lane));
    if (lane === this.lane) return;
    this.prevLane = this.lane;
    this.lane = lane;
  }

  /**
   * Giro pedido con el brazo (cámara): solo gira, nunca cambia de carril. Si aún no ha llegado a la
   * ventana de giro, la petición espera CONFIG.turn.requestHold segundos por si llega a tiempo.
   */
  requestTurn(dir) {
    if (this.state !== 'running') return;
    this.turnRequest = dir;
    this.turnRequestTime = CONFIG.turn.requestHold;
    this.#tryTurnRequest();
  }

  /**
   * Brazo levantado (cámara), dir: -1 izquierdo, +1 derecho. Cerca de una curva hacia ese lado (o de
   * un cruce) → gira, esperando si hace falta a llegar a la ventana de giro. En otro caso cambia de
   * carril; desde un carril exterior hacia fuera no hace nada (con la cámara nunca te tira al vacío).
   */
  requestSide(dir) {
    if (this.state !== 'running') return;
    const seg = this.segment;
    const reach = this.turnWindow + this.speed * CONFIG.turn.requestHold;
    const toCorner = this.toCorner;
    if ((seg.tee || seg.turn === dir) && toCorner <= reach && toCorner >= -CONFIG.turn.lateGrace) {
      this.requestTurn(dir);
      return;
    }
    this.setLane(this.lane + dir);
  }

  #tryTurnRequest() {
    const dir = this.turnRequest;
    const seg = this.segment;
    if (!dir || !(seg.tee || seg.turn === dir)) return;
    const toCorner = this.toCorner;
    if (toCorner <= this.turnWindow && toCorner >= -CONFIG.turn.lateGrace) {
      this.queuedTurn = dir;
      this.turnRequest = 0;
    }
  }

  #jump() {
    this.vy = P.jumpVelocity;
    this.grounded = false;
    this.slideTimer = 0;
    this.jumpBuffer = 0;
  }

  // ---------- Simulación ----------

  update(dt) {
    let event = this.pendingEvent;
    this.pendingEvent = null;
    switch (this.state) {
      case 'running':
        event = this.#updateRunning(dt) ?? event;
        break;
      case 'falling':
        this.#updateFalling(dt);
        break;
      case 'crashed':
      case 'caught':
        this.#updateCrashed(dt);
        break;
    }
    return event;
  }

  #updateRunning(dt) {
    const d = this.speed * dt;
    this.s += d;
    this.distance += d;
    this.#moveLateral(dt, this.lane * LW);
    // Sobre un foso no hay suelo: si no va por el aire, cae. Durante el sprint lo cruza a la carrera.
    let pit = pitUnder(this);
    if (pit && (pit.bridged || this.pitProof)) {
      pit.bridged = true;
      pit = null;
    }
    this.#applyVertical(dt, pit ? -Infinity : 0);
    if (pit && this.y < -0.05) {
      this.state = 'falling';
      this.fallPit = pit;
      this.slideTimer = 0;
      return 'pit';
    }
    this.slideTimer = Math.max(0, this.slideTimer - dt);
    this.jumpBuffer = Math.max(0, this.jumpBuffer - dt);
    this.hurtTimer = Math.max(0, this.hurtTimer - dt);
    if (this.turnRequest) {
      this.turnRequestTime -= dt;
      if (this.turnRequestTime <= 0) this.turnRequest = 0;
      else this.#tryTurnRequest();
    }

    const seg = this.segment;
    if (this.queuedTurn && this.s >= seg.length) {
      const dir = this.queuedTurn;
      const next = seg.tee ? seg.branches[dir < 0 ? 0 : 1] : seg.next;
      if (seg.tee) this.path.choose(seg, next);
      // Conversión geométrica exacta al nuevo tramo: sin saltos de posición.
      // El corredor conserva su carril: tras la conversión queda casi en el eje
      // y se desliza hasta él, como si trazara la curva.
      const over = this.s - seg.length;
      this.s = dir * this.x;
      this.x = -dir * over;
      this.prevLane = this.lane;
      this.segment = next;
      this.yaw -= (dir * Math.PI) / 2;
      this.queuedTurn = 0;
      this.turnRequest = 0;
      return 'turn';
    }
    if (this.s > seg.length + CONFIG.turn.lateGrace) {
      this.state = 'falling';
      return 'fell';
    }
    return this.#resolveHit(findHit(this));
  }

  /**
   * Qué pasa al tocar un obstáculo:
   *  - invulnerable (escudo/sprint) → lo rompe ('smash')
   *  - recién tropezado → se ignora unos instantes
   *  - de lado al cambiar de carril → rebota a su carril ('stumble')
   *  - de frente contra un tronco → tropieza y lo derriba ('stumble')
   *  - de frente contra una rama o una roca → muere ('crash')
   */
  #resolveHit(o) {
    if (!o) return null;
    this.lastHit = o;
    if (this.invulnerable) return 'smash';
    const changingLane = Math.abs(this.x - this.lane * LW) > 0.1;
    const prevX = this.prevLane * LW;
    const blocksOrigin = o.xMin < prevX && prevX < o.xMax;
    if (this.hurtTimer > 0) {
      // Recién tropezado: troncos y ramas se ignoran; contra la roca vuelve a rebotar, sin otro tropiezo.
      if (o.type === 'low' || o.type === 'high') o.hit = true;
      else if (changingLane && !blocksOrigin) this.lane = this.prevLane;
      return null;
    }
    if (changingLane && !blocksOrigin) {
      this.lane = this.prevLane;
      this.hurtTimer = CONFIG.stumble.hurtTime;
      return 'stumble';
    }
    if (o.type === 'low') {
      o.hit = true;
      this.hurtTimer = CONFIG.stumble.hurtTime;
      return 'stumble';
    }
    this.state = 'crashed';
    this.slideTimer = 0;
    return 'crash';
  }

  #updateFalling(dt) {
    if (this.fallPit) return this.#updateFallingInPit(dt);
    // Sigue avanzando (y hacia el lado si se salió por un lateral); fuera del suelo, cae.
    this.s += this.speed * dt;
    if (this.fallSide) this.#moveLateral(dt, this.fallSide * (HW + 1.5));
    const overVoid =
      Math.abs(this.x) - P.hitWidth / 2 > HW || this.s - P.width / 2 > this.segment.length + HW;
    this.#applyVertical(dt, overVoid ? -Infinity : 0);
    if (!overVoid) return;
    if (this.fallSide) this.body.rotation.z = Math.max(-1.2, Math.min(1.2, this.body.rotation.z - this.fallSide * 2.5 * dt));
    else this.body.rotation.x = Math.max(this.body.rotation.x - 2.5 * dt, -1.2);
  }

  /** Dentro de un foso: frena contra la pared del fondo, se centra en el hueco y cae dando una voltereta. */
  #updateFallingInPit(dt) {
    const pit = this.fallPit;
    this.speed *= Math.exp(-6 * dt);
    this.s = Math.min(this.s + this.speed * dt, pit.s1 - 0.45);
    const margin = Math.min(0.45, (pit.xMax - pit.xMin) / 2);
    this.x += (Math.max(pit.xMin + margin, Math.min(pit.xMax - margin, this.x)) - this.x) * Math.min(1, 8 * dt);
    this.#applyVertical(dt, -Infinity);
    this.body.rotation.x = Math.max(this.body.rotation.x - 3 * dt, -1.4);
  }

  #updateCrashed(dt) {
    this.#applyVertical(dt, 0);
    // Se cae de espaldas.
    this.body.rotation.x = Math.min(this.body.rotation.x + 5 * dt, Math.PI / 2);
  }

  #moveLateral(dt, target) {
    const step = P.laneChangeSpeed * dt;
    this.x += Math.max(-step, Math.min(step, target - this.x));
  }

  #applyVertical(dt, groundY) {
    if (this.grounded && groundY === 0) return;
    this.grounded = false;
    this.vy -= P.gravity * dt;
    this.y += this.vy * dt;
    if (this.y <= groundY) {
      this.y = groundY;
      this.vy = 0;
      this.grounded = true;
      if (this.jumpBuffer > 0 && this.state === 'running') this.#jump();
    }
  }

  // ---------- Presentación ----------

  /** Muestra los potenciadores activos (segundos restantes de cada uno). */
  setEffects(effects, time, grace = 0) {
    // En los últimos 2 s el escudo parpadea para avisar de que se acaba.
    this.shieldBubble.visible = effects.shield > 0 && (effects.shield > 2 || Math.sin(time * 20) > 0);
    this.magnetRing.visible = effects.magnet > 0;
    this.magnetRing.rotation.y = time * 4;
    this.torsoMaterial.emissive.copy(effects.sprint > 0 ? SPRINT_GLOW : this.model.baseGlow);
    // Parpadea tras un tropiezo, mientras es inmune a nuevos golpes.
    // También durante la invencibilidad al reaparecer.
    const blink = this.hurtTimer > 0 || (grace > 0 && effects.sprint === 0);
    this.body.visible = !(blink && Math.sin(time * 40) > 0);
  }

  syncMesh(dt) {
    this.path.worldPoint(this.segment, this.s, this.x, this.position);
    this.position.y = this.y;
    this.mesh.position.copy(this.position);
    this.path.worldPoint(this.segment, this.s, this.x * CONFIG.camera.lateralFollow, this.cameraAnchor);
    this.cameraAnchor.y = this.y;

    const k = dt > 0 ? 1 - Math.exp(-20 * dt) : 1;
    this.visualYaw += (this.yaw - this.visualYaw) * k;
    this.mesh.rotation.y = this.visualYaw;

    this.model.update(dt, this);
  }
}
